import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { verifyResetToken, resetPassword } from '../services/api';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        await verifyResetToken(token);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
        toast({
          title: 'Ongeldige Link',
          description: 'Deze reset link is ongeldig of verlopen',
          variant: 'destructive',
        });
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [token, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({
        title: 'Fout',
        description: 'Vul alle velden in',
        variant: 'destructive',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Fout',
        description: 'Wachtwoorden komen niet overeen',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: 'Fout',
        description: 'Wachtwoord moet minimaal 8 tekens zijn',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast({
        title: 'Wachtwoord Gewijzigd',
        description: 'Je kunt nu inloggen met je nieuwe wachtwoord',
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Er is een fout opgetreden',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-2xl border-2 border-[#FDB913]">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FDB913] mx-auto mb-4"></div>
            <p className="text-muted-foreground">Link verifiëren...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-2xl border-2 border-red-500">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-red-500 rounded-full p-4">
                <XCircle size={40} className="text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Ongeldige Link</h2>
            <p className="text-muted-foreground mb-6">
              Deze reset link is ongeldig of verlopen. Vraag een nieuwe link aan.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold">
                Nieuwe Link Aanvragen
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-2xl border-2 border-green-500">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-green-500 rounded-full p-4">
                <CheckCircle size={40} className="text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Wachtwoord Gewijzigd!</h2>
            <p className="text-muted-foreground mb-6">
              Je wachtwoord is succesvol gewijzigd. Je kunt nu inloggen met je nieuwe wachtwoord.
            </p>
            <Link to="/login">
              <Button className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold">
                Naar Inloggen
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md shadow-2xl border-2 border-[#FDB913]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#FDB913] rounded-full p-4">
              <Lock size={40} className="text-[#1a2332]" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-[#1a2332]">Nieuw Wachtwoord</CardTitle>
          <CardDescription>Kies een nieuw wachtwoord voor je account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="flex items-center space-x-2 mb-2">
                <Lock size={16} />
                <span>Nieuw Wachtwoord</span>
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Minimaal 8 tekens"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]"
                data-testid="reset-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="flex items-center space-x-2 mb-2">
                <Lock size={16} />
                <span>Bevestig Wachtwoord</span>
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Herhaal je wachtwoord"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]"
                data-testid="reset-password-confirm"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-semibold py-6"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? 'Opslaan...' : 'Wachtwoord Opslaan'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-[#FDB913] hover:text-[#e5a911] font-medium flex items-center justify-center space-x-2"
            >
              <ArrowLeft size={16} />
              <span>Terug naar Inloggen</span>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
